// @ts-nocheck
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {BaseAlert, BaseButton, BaseSpinner} from "@/components";
import {useBetbyWidgetManager} from "../../../hooks/useBetbyWidgetManager";
import useGetTokenBetby from "../../../hooks/useGetTokenBetby";
import useBetbyStore from "../../../store/Betby/useBetbyStore.store";
import {useAuthStore} from "../../../store/useAuth.store";

const BetbyWidgetTopOutrights = () => {
  const {t} = useTranslation();
  const lang = "es";

  // Extract currency from URL query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const currency = urlParams.get("currency") || "VES"; // Default to VES if no currency specified
  const authStatus = useAuthStore(state => state.authStatus);
  const tokenBetby = useBetbyStore(state => state.tokenBetby);

  // Use the hook instead of calling service directly
  const {isLoading: isTokenLoading, refetch: refetchToken} = useGetTokenBetby(
    lang,
    currency,
    authStatus === "authenticated"
  );

  // Stable callback handlers to prevent unnecessary re-renders
  const handleBannerClick = useCallback(args => {
    window.location.assign(`/sports?page=${args.url}`);
  }, []);

  const handleOutcomeClick = useCallback(args => {
    window.location.assign(`/sports?bt-path=${args.url}`);
  }, []);

  const handleRecharge = useCallback(() => {
    window.location.href = "/wallet";
  }, []);

  const handleLogin = useCallback(() => {
    useAuthStore.getState().handleAuthForm("login");
    useAuthStore.getState().handleShowAuthModal();
  }, []);

  const handleRegister = useCallback(() => {
    useAuthStore.getState().handleAuthForm("register");
    useAuthStore.getState().handleShowAuthModal();
  }, []);

  const handleGoToClick = useCallback(args => {
    window.location.assign(`/sports?bt-path=${args.url}`);
  }, []);

  // Create widget configuration with stable dependencies
  const widgetConfig = useMemo(() => {
    if (!tokenBetby || authStatus !== "authenticated" || isTokenLoading) {
      return null;
    }

    return {
      token: tokenBetby,
      brand_id: "2414868273263939584",
      lang: lang,
      themeName: "default",
      widgetName: "top_outright",
      widgetParams: {
        placeholder: "operator_page1",
        onBannerClick: handleBannerClick,
        onOutcomeClick: handleOutcomeClick,
        onRecharge: handleRecharge,
        onLogin: handleLogin,
        onRegister: handleRegister,
        goToClick: handleGoToClick,
        onError: _error => {},
      },
    };
  }, [
    tokenBetby,
    authStatus,
    isTokenLoading,
    lang,
    handleBannerClick,
    handleOutcomeClick,
    handleRecharge,
    handleLogin,
    handleRegister,
    handleGoToClick,
  ]);

  // Use the Betby widget manager
  const {
    containerRef,
    isLoading: isWidgetLoading,
    error,
    retryInitialization,
    setError,
  } = useBetbyWidgetManager(widgetConfig);

  // The component shouldn't render anything if the user is not authenticated
  if (authStatus !== "authenticated") {
    return null;
  }

  // Handle case where user is authenticated but token is not available
  if (authStatus === "authenticated" && !isTokenLoading && !tokenBetby) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "400px",
          padding: "var(--space-2-5)",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <BaseAlert
          variant="warning"
          title={t("tokenError", "Sports Token Error")}
        >
          <div style={{textAlign: "center"}}>
            <p style={{marginBottom: "var(--space-4-5)", fontSize: "var(--font-body-lg)", lineHeight: "1.4"}}>
              {t("tokenErrorDesc", "Unable to get sports betting token. This is required to load sports content.")}
            </p>
            <div style={{display: "flex", justifyContent: "center"}}>
              <BaseButton
                variant="primary"
                size="sm"
                onClick={() => refetchToken()}
              >
                {t("retry", "Retry")}
              </BaseButton>
            </div>
          </div>
        </BaseAlert>
      </div>
    );
  }

  const isLoading = isTokenLoading || isWidgetLoading;

  return (
    <div style={{width: "100%", minHeight: "400px"}}>
      {isLoading && (
        <div style={{display: "flex", justifyContent: "center", alignItems: "center", margin: "20px 0"}}>
          <BaseSpinner size="lg" />
        </div>
      )}

      {error && (
        <BaseAlert
          variant="danger"
          title={t("error")}
          onClose={() => setError(null)}
        >
          <div>
            <p style={{marginBottom: "var(--space-3-5)"}}>{error}</p>
            <BaseButton
              variant="danger"
              size="sm"
              onClick={retryInitialization}
            >
              {t("retry", "Retry")}
            </BaseButton>
          </div>
        </BaseAlert>
      )}

      <div
        ref={containerRef}
        style={{width: "100%", minHeight: "400px"}}
      />
    </div>
  );
};

export default BetbyWidgetTopOutrights;
